import BaseDataFilter from "@/core/js/backend/filters/BaseDataFilter";
import DataFilter from "@/core/js/backend/filters/DataFilter";
import {JournalEntryType} from "@shared/Journal/JournalEntryType";
import {ServiceType} from "@shared/Quote/ServiceType";
import {QuoteSectionId} from "@shared/Quote/QuoteSectionId";
import {InvoiceStatus} from "@shared/Invoice/InvoiceStatus";
import {isAppTheraLogik} from "@/utils/helpers";

const MIGRATION_PAGE_SIZE = 100
const DAY_MS = 24 * 60 * 60 * 1000
const UNBILLED_INVOICE_STATUSES = new Set([InvoiceStatus.NOT_SET, InvoiceStatus.PREVIEW, InvoiceStatus.DRAFT])

function resolveQuotedSessionGroup(sessionEntry, sessionGroups) {
	let legacyType = sessionEntry.legacyServiceType
	let matchingGroups = legacyType
		? sessionGroups.filter(group => group.legacyServiceType === legacyType)
		: sessionGroups.filter(group => group.sectionId === QuoteSectionId.DIRECT_WORK)
	return matchingGroups.length === 1 ? matchingGroups[0] : null
}

function getPotentiallyInvoicedSessions(invoice, lineItem, legacyType, sessionEntries, sessionGroups) {
	let sourceServiceIds = new Set()
	if (lineItem.sourceServiceId) sourceServiceIds.add(lineItem.sourceServiceId)
	else if (legacyType !== ServiceType.SESSION) {
		for (let group of sessionGroups) {
			if (group.legacyServiceType === legacyType) sourceServiceIds.add(group.id)
		}
	}
	let latestWorkDateMs = lineItem.serviceDateMs || invoice.sentDateMs || invoice.invoiceDateMs
	return sessionEntries.filter(session => {
		if (sourceServiceIds.size && !sourceServiceIds.has(session.sourceServiceId)) return false
		if (latestWorkDateMs && session.startTimeMs && session.startTimeMs > latestWorkDateMs + DAY_MS) return false
		let isUnbilled = !session.invoiceStatus || [InvoiceStatus.NOT_SET, InvoiceStatus.DRAFT].includes(session.invoiceStatus)
		return !session.isBillingDisconnected && isUnbilled
	})
}

async function migrateClientSessions(dataManager, clientProfile) {
	let quoteItem = clientProfile.getCommissionDetails()?.getQuoteItem()
	if (!quoteItem) return
	let sessionGroups = quoteItem.getAllServiceItems().filter(item => item.serviceType === ServiceType.SESSION)
	let sessionCountBySourceServiceId = {}
	let unresolvedSessionIds = []
	let totalSessionsCount = 0
	let sessionIds = new Set()
	let sessionEntriesById = new Map()
	let invoicedSessionUpdates = new Map()
	let potentiallyInvoicedSessionIds = new Set()
	let quarantinedSessionIds = new Set()
	let pager = await dataManager.getJournalEntryListPager(
		new DataFilter(MIGRATION_PAGE_SIZE)
			.where("clientId", "==", clientProfile.id)
			.where("type", "==", JournalEntryType.SESSION)
	)
	while (pager.hasMore) {
		let sessionEntries = await pager.next()
		let changedEntries = []
		for (let sessionEntry of sessionEntries) {
			totalSessionsCount++
			sessionIds.add(sessionEntry.id)
			sessionEntriesById.set(sessionEntry.id, sessionEntry)
			let wasUnlinked = !sessionEntry.sourceServiceId
			let hadLegacyType = !!sessionEntry.legacyServiceType
			if (!sessionEntry.sourceServiceId) {
				let group = resolveQuotedSessionGroup(sessionEntry, sessionGroups)
				if (group) sessionEntry.sourceServiceId = group.id
				else unresolvedSessionIds.push(sessionEntry.id)
			}
			if (sessionEntry.sourceServiceId) {
				sessionCountBySourceServiceId[sessionEntry.sourceServiceId] =
					(sessionCountBySourceServiceId[sessionEntry.sourceServiceId] || 0) + 1
			}
			if (sessionEntry.sourceServiceId && (hadLegacyType || wasUnlinked)) {
				delete sessionEntry.legacyServiceType
				changedEntries.push(sessionEntry)
			}
		}
		if (changedEntries.length) await dataManager.batchUpdateJournalEntry(changedEntries)
	}
	let sessionEntries = [...sessionEntriesById.values()]
	let invoicePager = await dataManager.getInvoiceItemListPager(
		new DataFilter(MIGRATION_PAGE_SIZE).where("clientId", "==", clientProfile.id)
	)
	while (invoicePager.hasMore) {
		let invoices = await invoicePager.next()
		for (let invoice of invoices) {
			let changed = false
			for (let lineItem of invoice.lineItems || []) {
				let legacyType = lineItem.serviceType
				let isSessionLine = legacyType === ServiceType.SESSION || quoteItem.isLegacySessionServiceType(legacyType)
				if (quoteItem.isLegacySessionServiceType(lineItem.serviceType)) {
					lineItem.serviceType = ServiceType.SESSION
					changed = true
				}
				let draftIds = Array.isArray(lineItem._draftLineItemIds) ? lineItem._draftLineItemIds : []
				let linkedSessionIds = lineItem.sourceSessionEntryId
					? [lineItem.sourceSessionEntryId]
					: draftIds.filter(id => sessionIds.has(id))
				if (!linkedSessionIds.length && sessionIds.has(lineItem.id)) linkedSessionIds = [lineItem.id]
				if (linkedSessionIds.length !== 1) {
					if (isSessionLine && !UNBILLED_INVOICE_STATUSES.has(invoice.status)) {
						let candidates = getPotentiallyInvoicedSessions(invoice, lineItem, legacyType,
							sessionEntries, sessionGroups)
						for (let session of candidates) potentiallyInvoicedSessionIds.add(session.id)
					}
					continue
				}
				if (!lineItem.sourceSessionEntryId) {
					lineItem.sourceSessionEntryId = linkedSessionIds[0]
					changed = true
				}
				for (let id of linkedSessionIds) {
					if (!sessionIds.has(id)) continue
					let previous = invoicedSessionUpdates.get(id)
					if (previous && previous.invoiceStatus !== InvoiceStatus.DRAFT
						&& invoice.status === InvoiceStatus.DRAFT) continue
					invoicedSessionUpdates.set(id, {id, invoiceId: invoice.id, invoiceStatus: invoice.status})
				}
			}
			if (changed) await dataManager.updateInvoiceItem(invoice)
		}
	}
	for (let id of potentiallyInvoicedSessionIds) {
		if (invoicedSessionUpdates.has(id)) continue
		let session = sessionEntriesById.get(id)
		session.isBillingDisconnected = true
		session.isBillingReconciliationRequired = true
		quarantinedSessionIds.add(id)
		invoicedSessionUpdates.set(id, {
			id, isBillingDisconnected: true, isBillingReconciliationRequired: true,
		})
	}
	if (invoicedSessionUpdates.size) {
		await dataManager.batchUpdateJournalEntry([...invoicedSessionUpdates.values()])
	}
	let draftPager = await dataManager.getDraftLineItemListPager(
		new DataFilter(MIGRATION_PAGE_SIZE).where("clientId", "==", clientProfile.id)
	)
	while (draftPager.hasMore) {
		let drafts = await draftPager.next()
		for (let draft of drafts) {
			let lineItem = draft.lineItem
			let changed = false
			if (quarantinedSessionIds.has(lineItem?.sourceSessionEntryId || lineItem?.id)
				&& !draft.isBillingSuspended) {
				draft.isBillingSuspended = true
				changed = true
			}
			if (quoteItem.isLegacySessionServiceType(lineItem?.serviceType)) {
				lineItem.serviceType = ServiceType.SESSION
				changed = true
			}
			if (sessionIds.has(lineItem?.id) && !lineItem.sourceSessionEntryId) {
				lineItem.sourceSessionEntryId = lineItem.id
				changed = true
			}
			if (changed) await dataManager.updateDraftLineItem(draft)
		}
	}
	if (!unresolvedSessionIds.length) {
		for (let group of sessionGroups) delete group.legacyServiceType
	}
	let progressInfo = clientProfile.getCommissionDetails()?.progressInfo
	if (progressInfo) {
		progressInfo.sessionCountBySourceServiceId = sessionCountBySourceServiceId
		progressInfo.totalSessionsCount = totalSessionsCount
	}
	await dataManager.updateClientProfile(clientProfile)
	if (unresolvedSessionIds.length) {
		console.warn("SESSION_SERVICE_MIGRATION unresolved", JSON.stringify({
			clientId: clientProfile.id,
			sessionIds: unresolvedSessionIds,
		}))
	}
}

export async function migrateSessionServiceLinks(dataManager, authManager) {
	if (!isAppTheraLogik || !authManager.isCoach()) return
	let pager = await dataManager.getClientProfileListPager(new BaseDataFilter(MIGRATION_PAGE_SIZE))
	while (pager.hasMore) {
		let clientProfiles = await pager.next()
		for (let clientProfile of clientProfiles) {
			await migrateClientSessions(dataManager, clientProfile)
		}
	}
}
